import { useState, useEffect } from 'react';
import EmptyState from './EmptyState';

/**
 * Base component for admin detail views
 * Provides consistent structure for User, Product, AI config detail pages
 *
 * @param {object} item - The item to display (user, product, config, etc.)
 * @param {string} emptyStateIcon - SVG path for empty state icon
 * @param {string} emptyStateTitle - Title for empty state
 * @param {string} emptyStateSubtitle - Optional subtitle for empty state
 * @param {object} emptyStateAction - Optional action button for empty state { label, onClick }
 * @param {ReactNode} headerContent - Content for the header (title, avatar, badges)
 * @param {ReactNode} headerActions - Action buttons for the header
 * @param {Array} tabs - Optional array of tab objects { id, label, icon, shortLabel }
 * @param {string} defaultTab - Default active tab (only used if activeTab is not provided)
 * @param {string} activeTab - Controlled active tab (optional - for external state management)
 * @param {Function} onTabChange - Callback when tab changes (optional - for external state management)
 * @param {Function} renderContent - Function to render content based on active tab
 */
export default function BaseDetailView({
  item,
  emptyStateIcon,
  emptyStateTitle,
  emptyStateSubtitle,
  emptyStateAction,
  headerContent,
  headerActions,
  tabs = [],
  defaultTab = null,
  activeTab: externalActiveTab,
  onTabChange,
  renderContent
}) {
  const [internalActiveTab, setInternalActiveTab] = useState(defaultTab || tabs[0]?.id);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Update window width on resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Use controlled tab if provided, otherwise use internal state
  const activeTab = externalActiveTab !== undefined ? externalActiveTab : internalActiveTab;
  const handleTabChange = (tabId) => {
    if (onTabChange) {
      onTabChange(tabId);
    } else {
      setInternalActiveTab(tabId);
    }
  };

  // If no item, show empty state
  if (!item) {
    return (
      <EmptyState
        icon={emptyStateIcon}
        title={emptyStateTitle}
        subtitle={emptyStateSubtitle}
        action={emptyStateAction}
      />
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header Section */}
      <div className="border-b px-6 pt-4 flex-shrink-0 relative overflow-hidden" style={{ borderColor: 'var(--color-border-default)', background: 'linear-gradient(135deg, var(--color-gradient-from) 0%, var(--color-gradient-via) 50%, var(--color-gradient-to) 100%)' }}>
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'linear-gradient(var(--color-border-subtle) 1px, transparent 1px), linear-gradient(90deg, var(--color-border-subtle) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          opacity: 0.3
        }}></div>

        {/* Content wrapper for z-index */}
        <div className="relative z-10">
        <div className="flex items-start justify-between mb-4">
          {/* Header Content (title, badges, etc.) */}
          <div className="flex-1">
            {headerContent}
          </div>

          {/* Header Actions (buttons) */}
          {headerActions && (
            <div className="flex gap-2">
              {headerActions}
            </div>
          )}
        </div>

        {/* Tabs (if provided) */}
        {tabs.length > 0 && (
          <div className="flex gap-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center ${windowWidth > 650 ? 'gap-2' : ''} px-4 py-2 rounded-t-lg transition-colors duration-200 ${
                  activeTab === tab.id
                    ? 'border-t border-x'
                    : ''
                }`}
                style={
                  activeTab === tab.id
                    ? {
                        backgroundColor: 'var(--color-bg-primary)',
                        borderColor: 'var(--color-border-default)',
                        color: 'var(--color-text-primary)'
                      }
                    : {
                        color: 'var(--color-text-muted)'
                      }
                }
                onMouseEnter={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                    e.currentTarget.style.color = 'var(--color-text-secondary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = 'var(--color-text-muted)';
                  }
                }}
              >
                {windowWidth > 650 && (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                  </svg>
                )}
                <span className="font-medium">{windowWidth > 650 ? tab.label : (tab.shortLabel || tab.label)}</span>
              </button>
            ))}
          </div>
        )}
        </div>
      </div>

      {/* Content Section */}
      <div className="flex-1 overflow-auto" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
        {renderContent(activeTab)}
      </div>
    </div>
  );
}
